import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { accountSearchColumns, khachHangSearchColumns, QUERY_KEYS, thueSearchColumns } from '@/constants';
import type { AccountModel, KhachHang, PhiNganHang, Tax, TinhChatThue } from '@/types/schemas';
import { FormField, SearchField } from '@/components/custom/erp';
import type { SearchFormValues } from '../../schemas';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';
import { useCRUD } from '@/hooks';

interface FormProps {
  mode: FormMode;
}

type AccountSelection = Pick<AccountModel, 'uuid' | 'code' | 'name'>;
type TaxSelection = Pick<Tax, 'ma_thue' | 'ten_thue'> & {
  uuid?: string | null;
};
type CustomerSelection = Pick<KhachHang, 'customer_name'> & {
  uuid?: string;
  customer_code?: string;
  code?: string;
};

function BankFeeForm({ mode }: FormProps) {
  const isDisabled = mode === 'view';
  const { setValue, getValues } = useFormContext<SearchFormValues & Partial<PhiNganHang>>();

  const { data: tinhChatThueData } = useCRUD<TinhChatThue>({ endpoint: QUERY_KEYS.TINH_CHAT_THUE });
  const tinhChatThueOptions = tinhChatThueData.map(item => ({
    value: item.uuid,
    label: item.ten_tc_thue
  }));

  const [selectionData, setSelectionData] = useState<{
    tkPhi?: AccountSelection;
    maThue?: TaxSelection;
    tkThue?: AccountSelection;
    cqt?: CustomerSelection;
  }>({});

  useEffect(() => {
    if (tinhChatThueOptions.length > 0) {
      const currentValue = getValues('ma_tc_thue');
      if (!currentValue) {
        setValue('ma_tc_thue', tinhChatThueOptions[0].value);
      }
    }
  }, [tinhChatThueOptions, setValue, getValues, mode]);

  useEffect(() => {
    const formValues = getValues();
    if (formValues) {
      const tkCpnhData = formValues.tk_cpnh_data;
      const maThueData = formValues.ma_thue_data;
      const tkThueData = formValues.tk_thue_data;
      const maKh9Data = formValues.ma_kh9_data;

      if (formValues.tk_cpnh && tkCpnhData) {
        setSelectionData(prev => ({
          ...prev,
          tkPhi: {
            uuid: formValues.tk_cpnh,
            code: tkCpnhData.code,
            name: tkCpnhData.name
          }
        }));
      }

      if (formValues.ma_thue && maThueData) {
        setSelectionData(prev => ({
          ...prev,
          maThue: {
            uuid: formValues.ma_thue,
            ma_thue: maThueData.ma_thue,
            ten_thue: maThueData.ten_thue
          }
        }));
      }

      if (formValues.tk_thue && tkThueData) {
        setSelectionData(prev => ({
          ...prev,
          tkThue: {
            uuid: formValues.tk_thue,
            code: tkThueData.code,
            name: tkThueData.name
          }
        }));
      }
      if (formValues.ma_kh9 && maKh9Data) {
        setSelectionData(prev => ({
          ...prev,
          cqt: {
            uuid: formValues.ma_kh9,
            customer_code: maKh9Data.customer_code,
            customer_name: maKh9Data.customer_name
          }
        }));
      }
    }
  }, [mode, getValues]);

  const handleTkPhiSelection = (row: AccountModel) => {
    setSelectionData(prev => ({ ...prev, tkPhi: row }));
    setValue('tk_cpnh', row.uuid || row.code);
  };

  const handleCqtSelection = (row: CustomerSelection) => {
    setSelectionData(prev => ({ ...prev, cqt: row }));
    setValue('ma_kh9', row.uuid || row.code);
  };

  const handleMaThueSelection = (row: Tax) => {
    setSelectionData(prev => ({ ...prev, maThue: row }));
    setValue('ma_thue', row?.uuid || row?.ma_thue || null);
  };

  const handleTkThueSelection = (row: AccountModel) => {
    setSelectionData(prev => ({ ...prev, tkThue: row }));
    setValue('tk_thue', row.uuid || row.code);
  };

  return (
    <div className='space-y-2 p-4'>
      <div className='flex flex-col sm:flex-row sm:items-center'>
        <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Mã phí</Label>
        <FormField name='ma_cpnh' disabled={isDisabled || mode === 'edit'} />
      </div>

      <div className='flex flex-col sm:flex-row sm:items-center'>
        <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Tên phí</Label>
        <div className='w-full'>
          <FormField name='ten_cpnh' disabled={isDisabled} />
        </div>
      </div>

      <div className='flex flex-col sm:flex-row sm:items-center'>
        <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Tên khác</Label>
        <div className='w-full'>
          <FormField name='ten_cpnh2' disabled={isDisabled} />
        </div>
      </div>

      <div className='flex items-center'>
        <Label className='w-40 min-w-40'>Tài khoản phí</Label>
        <SearchField<AccountModel>
          columnDisplay='code'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          dialogTitle='Danh mục tài khoản'
          value={selectionData.tkPhi?.code || ''}
          relatedFieldValue={selectionData.tkPhi?.name || ''}
          displayRelatedField='name'
          onRowSelection={handleTkPhiSelection}
          disabled={isDisabled}
          className='w-100'
        />
      </div>

      <div className='flex items-center'>
        <Label className='w-40 min-w-40'>Mã thuế</Label>
        <SearchField<Tax>
          disabled={mode === 'view'}
          searchEndpoint={`/${QUERY_KEYS.THUE}/`}
          searchColumns={thueSearchColumns}
          dialogTitle='Danh mục thuế'
          value={selectionData.maThue?.ma_thue || ''}
          relatedFieldValue={selectionData.maThue?.ten_thue || ''}
          displayRelatedField='ten_thue'
          onRowSelection={handleMaThueSelection}
          className='w-100'
        />
      </div>

      <div className='flex items-center'>
        <Label className='w-40 min-w-40'>Tài khoản thuế</Label>
        <SearchField<AccountModel>
          columnDisplay='code'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          dialogTitle='Danh mục tài khoản'
          value={selectionData.tkThue?.code || ''}
          relatedFieldValue={selectionData.tkThue?.name || ''}
          displayRelatedField='name'
          onRowSelection={handleTkThueSelection}
          disabled={isDisabled}
          className='w-100'
        />
      </div>

      <div className='flex items-center'>
        <Label className='w-40 min-w-40'>Cục thuế</Label>
        <SearchField<KhachHang>
          disabled={mode === 'view'}
          searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
          searchColumns={khachHangSearchColumns}
          dialogTitle='Danh mục đối tượng'
          displayRelatedField='customer_name'
          value={selectionData.cqt?.customer_code || ''}
          relatedFieldValue={selectionData.cqt?.customer_name || ''}
          onRowSelection={handleCqtSelection}
          className='w-100'
        />
      </div>

      <div className='flex flex-col sm:flex-row sm:items-center'>
        <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Mẫu báo cáo</Label>
        <FormField
          type='select'
          name='ma_mau_bc'
          disabled={isDisabled}
          options={[
            { value: '3', label: '3. Hóa đơn giá trị gia tăng' },
            { value: '4', label: '4. Hàng hóa, dịch vụ mua vào không có hóa đơn' },
            { value: '5', label: '5. Hóa đơn bán hàng thông thường' }
          ]}
        />
      </div>

      <div className='flex flex-col sm:flex-row sm:items-center'>
        <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Mã tính chất</Label>
        <div className='w-96'>
          <FormField type='select' name='ma_tc_thue' disabled={isDisabled} options={tinhChatThueOptions} />
        </div>
      </div>

      <div className='flex flex-col sm:flex-row sm:items-center'>
        <Label htmlFor='status' className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>
          Trạng thái
        </Label>
        <FormField
          id='status'
          type='select'
          name='status'
          options={[
            { label: '1. Còn sử dụng', value: '1' },
            { label: '0. Không sử dụng', value: '0' }
          ]}
          disabled={isDisabled}
        />
      </div>
    </div>
  );
}

export default BankFeeForm;
